import React from 'react';
import { Complaint } from '../../types/complaint';
import { StatusBadge, PriorityBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { SLABadge } from '../sla/SLABadge';
import { Eye, Clock } from 'lucide-react';

interface ComplaintListProps {
  complaints: Complaint[];
  onSelect: (complaint: Complaint) => void;
  viewMode: 'table' | 'cards';
}

export const ComplaintTable: React.FC<ComplaintListProps> = ({ complaints, onSelect, viewMode }) => {
  if (complaints.length === 0) {
    return (
      <div className="p-8 text-center bg-slate-900 border border-slate-800 rounded-xl text-slate-400">
        No complaints match your active search filters.
      </div>
    );
  }

  if (viewMode === 'cards') {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {complaints.map(c => (
          <div key={c.complaint_id} className="bg-slate-800 border border-slate-700/60 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs text-sky-400 font-semibold">{c.complaint_id}</span>
              <StatusBadge status={c.status_name} />
            </div>
            <div>
              <h4 className="font-semibold text-slate-100 text-sm">{c.title}</h4>
              <p className="text-xs text-slate-400 line-clamp-2 mt-1">{c.description}</p>
            </div>
            <div className="flex items-center justify-between text-xs text-slate-400 border-t border-slate-700/50 pt-2">
              <div className="flex items-center gap-2">
                <PriorityBadge priority={c.priority_name} />
                <SLABadge createdAt={c.created_at} priorityName={c.priority_name} compact />
              </div>
              <Button size="sm" variant="ghost" onClick={() => onSelect(c)} icon={<Eye className="w-3.5 h-3.5" />}>
                View
              </Button>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto bg-slate-900 border border-slate-800 rounded-xl">
      <table className="w-full text-left text-xs text-slate-300">
        <thead className="bg-slate-950 text-slate-400 font-semibold border-b border-slate-800 uppercase">
          <tr>
            <th className="px-4 py-3">ID</th>
            <th className="px-4 py-3">Title / Scope</th>
            <th className="px-4 py-3">Department</th>
            <th className="px-4 py-3">Category</th>
            <th className="px-4 py-3">Priority</th>
            <th className="px-4 py-3">SLA</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3 text-right">Action</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800/60">
          {complaints.map(c => (
            <tr key={c.complaint_id} className="hover:bg-slate-850 transition-colors">
              <td className="px-4 py-3 font-mono font-semibold text-sky-400">{c.complaint_id}</td>
              <td className="px-4 py-3">
                <p className="font-medium text-slate-100">{c.title}</p>
                <p className="text-[10px] text-slate-500">{c.location_name}</p>
              </td>
              <td className="px-4 py-3 text-slate-300">{c.department_name}</td>
              <td className="px-4 py-3 text-slate-300">{c.category_name}</td>
              <td className="px-4 py-3"><PriorityBadge priority={c.priority_name} /></td>
              <td className="px-4 py-3"><SLABadge createdAt={c.created_at} priorityName={c.priority_name} compact /></td>
              <td className="px-4 py-3"><StatusBadge status={c.status_name} /></td>
              <td className="px-4 py-3 text-right">
                <Button size="sm" variant="ghost" onClick={() => onSelect(c)} icon={<Eye className="w-3.5 h-3.5" />}>
                  Details
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
